import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { formatPercent } from '@athena/i18n';
import { cn } from '../../lib/cn.js';
import { useLanguage } from '../../lib/i18n.js';
const PLOT_TOP = 14;
const PLOT_HEIGHT = 72;
function clampPercent(value) {
    return Math.min(100, Math.max(0, value));
}
function pointY(percent) {
    return Number((PLOT_TOP + ((100 - clampPercent(percent)) / 100) * PLOT_HEIGHT).toFixed(2));
}
function pointX(index, count) {
    return Number((((index + 0.5) / count) * 100).toFixed(4));
}
/**
 * One practice's submitted scores, from the first attempt to the latest.
 *
 * This is deliberately lighter than Athena's analytical chart: one progress
 * line, one visible point and score per attempt, no axes, grid, legend,
 * tooltip or interaction. The line answers only whether this one practice's
 * scores moved; it never joins unrelated practices or assigns a verdict.
 *
 * The visual plot is decorative. The canonical representation is an ordered,
 * named list whose items read "Attempt 3: 85%, Latest", so a screen reader
 * receives the same sequence and recency cue without interpreting geometry or
 * colour. Points are not focusable because they do not perform an action.
 */
export function ScoreSequence({ items, label, latestLabel, className }) {
    const language = useLanguage();
    const dense = items.length > 6;
    const gridTemplateColumns = `repeat(${Math.max(items.length, 1)}, minmax(0, 1fr))`;
    const linePoints = items
        .map((item, index) => {
        const x = pointX(index, items.length);
        return `${x},${pointY(item.percent)}`;
    })
        .join(' ');
    if (items.length === 0)
        return null;
    return (_jsxs("ol", { "aria-label": label, className: cn('relative grid min-w-0', className), style: { gridTemplateColumns }, children: [items.length > 1 ? (_jsx("svg", { "aria-hidden": "true", className: "pointer-events-none absolute inset-x-0 top-6 h-16 w-full overflow-visible text-accent-text", viewBox: "0 0 100 100", preserveAspectRatio: "none", children: _jsx("polyline", { "data-score-line": "true", points: linePoints, fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", vectorEffect: "non-scaling-stroke" }) })) : null, items.map((item, index) => {
                const isLatest = index === items.length - 1;
                const percent = formatPercent(item.percent, language);
                return (_jsxs("li", { className: "relative grid min-w-0 grid-rows-[1.5rem_4rem_auto_auto] justify-items-center text-center", children: [_jsx("span", { className: "sr-only", children: `${item.label}: ${percent}${isLatest && latestLabel ? `, ${latestLabel}` : ''}` }), _jsx("span", { "aria-hidden": "true", className: "max-w-full text-caption font-semibold tabular-nums text-text", children: percent }), _jsx("span", { "aria-hidden": "true", className: "relative h-16 w-full", children: _jsx("span", { "data-score-point": "true", "data-latest": isLatest ? 'true' : undefined, className: cn('absolute left-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full', 'border-2 border-accent-text bg-accent', isLatest &&
                                    'size-4 border-primary ring-2 ring-primary ring-offset-2 ring-offset-background'), style: { top: `${pointY(item.percent)}%` } }) }), _jsx("span", { "aria-hidden": "true", className: "max-w-full text-caption font-medium leading-tight text-text-secondary", children: dense ? (item.shortLabel ?? item.label) : item.label }), isLatest && latestLabel ? (_jsx("span", { "aria-hidden": "true", className: "text-caption font-semibold text-primary", children: latestLabel })) : null] }, item.id));
            })] }));
}
//# sourceMappingURL=ScoreSequence.js.map